import { useMutation, useQueryClient } from '@tanstack/react-query';
import { emails, fetchFlow } from '@/lib/api/email-flows';
import type { FlowStatus } from '@/lib/api/email-flows/types';
import { QUERY_KEYS, stanAnalytics } from '@/lib';
import { useUser } from '@/queries';
import type { EmailFormData } from '../types/email.types';
import { checkEmailModifiability } from '../logic/emailPermissions.logic';
import { useEmailEditorStore } from '../stores';

/**
 * Mutation hook for saving (creating/updating) a flow email
 */
export function useSaveEmail(flowId: number, emailId?: number) {
    const queryClient = useQueryClient();
    const { user } = useUser();
    const flowData = useEmailEditorStore(state => state.flowData);

    return useMutation({
        mutationFn: async ({ data, status }: { data: EmailFormData; status: FlowStatus }) => {
            // Check if email can be modified before making API call
            const modifiabilityCheck = checkEmailModifiability(flowData, data.delay, data.delayUnit, status);

            if (!modifiabilityCheck.modifiable) {
                throw new Error(modifiabilityCheck.message);
            }

            // Parse delay value, defaulting to 0 for empty strings (drafts)
            const delayValue = data.delay && data.delay !== '' ? parseInt(data.delay, 10) : 0;

            const payload: {
                flow_id: number;
                flow_email_id?: number;
                status: string;
                data: {
                    delay: number;
                    delay_unit: string;
                    subject: string;
                    body: string;
                };
            } = {
                flow_id: flowId,
                status,
                data: {
                    delay: delayValue,
                    delay_unit: data.delayUnit,
                    subject: data.subject,
                    body: data.body,
                },
            };

            // Only include flow_email_id if we're updating an existing email
            if (emailId) {
                payload.flow_email_id = emailId;
            }

            try {
                return await emails.upsertFlowEmail(payload);
            } catch (error: any) {
                // BACKEND BUG WORKAROUND: The backend returns a 500 error even when the email saves successfully.
                // This happens in the create_update_flow_email endpoint after the save.
                // The email IS saved to the database, so we verify by refetching.
                if (error.response?.status === 500) {
                    // Refetch the flow to verify if the email was actually saved
                    const updatedFlow = await fetchFlow(flowId);
                    const savedEmail = emailId
                        ? updatedFlow.flow_emails?.find((e: any) => e.flow_email_id === emailId)
                        : updatedFlow.flow_emails?.[updatedFlow.flow_emails.length - 1];

                    if (savedEmail) {
                        // Email was saved successfully despite the 500 error
                        // Return the saved email to continue as if it succeeded
                        return savedEmail;
                    }
                }
                // If it's not a 500 error or we couldn't verify the save, rethrow
                throw error;
            }
        },
        onSuccess: savedEmail => {
            // Invalidate queries
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_DETAILS, flowId] });
            if (emailId) {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_DETAILS, emailId] });
            }

            // Track analytics
            const eventName = emailId ? 'flow-email-updated' : 'flow-email-created';
            stanAnalytics(eventName, {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { flow_id: flowId, flow_email_id: savedEmail.flow_email_id },
            });

            return savedEmail;
        },
    });
}

/**
 * Mutation hook for deleting a flow email
 */
export function useDeleteEmail(flowId: number, emailId?: number) {
    const queryClient = useQueryClient();
    const { user } = useUser();

    return useMutation({
        mutationFn: () => {
            if (!emailId) throw new Error('Email ID is required');
            return emails.deleteFlowEmail(emailId);
        },
        onSuccess: () => {
            // Invalidate queries to refresh flow data (including email list in navigation)
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_DETAILS, flowId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });

            // Track analytics
            stanAnalytics('flow-email-deleted', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { flow_id: flowId, flow_email_id: emailId },
            });
        },
    });
}

/**
 * Mutation hook for sending a test email
 */
export function useSendTestEmail(flowId: number, emailId?: number) {
    const { user } = useUser();

    return useMutation({
        mutationFn: async ({ emailAddress, formData }: { emailAddress: string; formData: EmailFormData }) => {
            return emails.sendTestEmail({
                data: {
                    subject: formData.subject,
                    body: formData.body,
                },
                email_address: emailAddress,
            });
        },
        onSuccess: () => {
            // Track analytics
            stanAnalytics('flow-email-test-send', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: { flow_id: flowId, flow_email_id: emailId },
            });
        },
    });
}
